import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../config'

/**
 * MainMenuScene - The main menu screen
 * Cooking Mama style: colorful, inviting, with clear play button
 */
export class MainMenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MainMenuScene' })
  }

  create(): void {
    this.createBackground()
    this.createCharacter()
    this.createTitle()
    this.createMenuButtons()
    this.createDecorations()
  }

  private createBackground(): void {
    // Gradient background effect
    const graphics = this.add.graphics()

    // Create a warm gradient (top to bottom)
    const topColor = Phaser.Display.Color.HexStringToColor('#ff9a56')
    const bottomColor = Phaser.Display.Color.HexStringToColor('#ff6b6b')

    for (let y = 0; y < GAME_HEIGHT; y++) {
      const ratio = y / GAME_HEIGHT
      const color = Phaser.Display.Color.Interpolate.ColorWithColor(
        topColor,
        bottomColor,
        100,
        ratio * 100
      )
      graphics.fillStyle(Phaser.Display.Color.GetColor(color.r, color.g, color.b))
      graphics.fillRect(0, y, GAME_WIDTH, 1)
    }
  }

  private createTitle(): void {
    // Game title with playful styling
    const titleText = this.add.text(GAME_WIDTH / 2, 120, '🍳 Marmelade', {
      fontFamily: 'system-ui',
      fontSize: '64px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#e65100',
      strokeThickness: 8,
      shadow: {
        offsetX: 4,
        offsetY: 4,
        color: '#00000033',
        blur: 10,
        fill: true,
      },
    })
    titleText.setOrigin(0.5)

    // Subtitle
    const subtitleText = this.add.text(GAME_WIDTH / 2, 180, 'A Cooking Adventure', {
      fontFamily: 'system-ui',
      fontSize: '24px',
      color: '#fff5e6',
    })
    subtitleText.setOrigin(0.5)

    // Add bounce animation to title
    this.tweens.add({
      targets: titleText,
      y: titleText.y - 10,
      duration: 1500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    })
  }

  private createMenuButtons(): void {
    const buttonY = 320

    // Play button (original drag-drop demo)
    const playButton = this.createButton(
      GAME_WIDTH / 2,
      buttonY,
      '▶ Play',
      () => this.startGame()
    )

    // Chopping Mini-Game button (Strawberry Jam first step)
    const choppingButton = this.createButton(
      GAME_WIDTH / 2,
      buttonY + 80,
      '🍓 Chop Strawberries',
      () => this.startChoppingMiniGame(),
      { width: 240, fontSize: 20 }
    )

    // Test Mini-Game button
    const testButton = this.createButton(
      GAME_WIDTH / 2,
      buttonY + 150,
      '🎯 Test Mini-Game',
      () => this.startTestMiniGame(),
      { width: 220, fontSize: 18 }
    )

    // Add entrance animations
    playButton.setScale(0)
    choppingButton.setScale(0)
    testButton.setScale(0)

    this.tweens.add({
      targets: playButton,
      scale: 1,
      duration: 500,
      ease: 'Back.easeOut',
      delay: 200,
    })

    this.tweens.add({
      targets: choppingButton,
      scale: 1,
      duration: 500,
      ease: 'Back.easeOut',
      delay: 350,
    })

    this.tweens.add({
      targets: testButton,
      scale: 1,
      duration: 500,
      ease: 'Back.easeOut',
      delay: 450,
    })

    // Instructions text
    const instructionsText = this.add.text(
      GAME_WIDTH / 2,
      buttonY + 230,
      'Tap & drag ingredients to cook!',
      {
        fontFamily: 'system-ui',
        fontSize: '18px',
        color: '#fff5e6',
      }
    )
    instructionsText.setOrigin(0.5)
    instructionsText.setAlpha(0)

    this.tweens.add({
      targets: instructionsText,
      alpha: 1,
      duration: 800,
      delay: 600,
    })
  }

  private createButton(
    x: number,
    y: number,
    text: string,
    callback: () => void,
    options?: { width?: number; fontSize?: number }
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y)
    const width = options?.width ?? 200
    const fontSize = options?.fontSize ?? 28

    // Button background
    const bg = this.add.rectangle(0, 0, width, 60, 0xffffff, 0.9)
    bg.setStrokeStyle(4, 0xe65100)

    // Button text
    const buttonText = this.add.text(0, 0, text, {
      fontFamily: 'system-ui',
      fontSize: `${fontSize}px`,
      fontStyle: 'bold',
      color: '#e65100',
    })
    buttonText.setOrigin(0.5)

    container.add([bg, buttonText])

    // Make interactive
    bg.setInteractive({ useHandCursor: true })
      .on('pointerover', () => {
        this.tweens.add({
          targets: container,
          scale: 1.1,
          duration: 100,
        })
        bg.setFillStyle(0xffe0b2, 1)
      })
      .on('pointerout', () => {
        this.tweens.add({
          targets: container,
          scale: 1,
          duration: 100,
        })
        bg.setFillStyle(0xffffff, 0.9)
      })
      .on('pointerdown', () => {
        this.tweens.add({
          targets: container,
          scale: 0.95,
          duration: 50,
        })
      })
      .on('pointerup', () => {
        this.tweens.add({
          targets: container,
          scale: 1.1,
          duration: 50,
          onComplete: callback,
        })
      })

    return container
  }

  private createCharacter(): void {
    // Marmelade character - waving on the left side
    const marmelade = this.add.image(120, GAME_HEIGHT - 100, 'marmelade_waving')
    marmelade.setOrigin(0.5, 1)

    // Scale to fit nicely (image is 804x1594)
    const targetHeight = 350
    const scale = targetHeight / marmelade.height
    marmelade.setScale(scale)

    // Set depth to ensure visibility above background
    marmelade.setDepth(10)

    // Entrance animation - slide in from left
    marmelade.x = -100
    marmelade.setAlpha(0)

    this.tweens.add({
      targets: marmelade,
      x: 120,
      alpha: 1,
      duration: 600,
      ease: 'Back.easeOut',
      delay: 100,
    })

    // Gentle idle animation - slight bounce
    this.tweens.add({
      targets: marmelade,
      y: marmelade.y - 5,
      duration: 1200,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      delay: 700,
    })
  }

  private createDecorations(): void {
    // Add floating food emoji decorations
    const emojis = ['🍅', '🥕', '🧅', '🥬', '🍳', '🥄']

    emojis.forEach((emoji, index) => {
      const x = 80 + (index % 3) * 300 + Math.random() * 100
      const y = 450 + Math.floor(index / 3) * 80 + Math.random() * 50
      const text = this.add.text(x, y, emoji, { fontSize: '40px' })
      text.setOrigin(0.5)

      // Floating animation
      this.tweens.add({
        targets: text,
        y: text.y - 20,
        rotation: Phaser.Math.DegToRad(10),
        duration: 2000 + Math.random() * 1000,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 500,
      })
    })
  }

  private startGame(): void {
    // Transition to game scene
    this.cameras.main.fadeOut(300, 0, 0, 0)
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GameScene')
    })
  }

  private startTestMiniGame(): void {
    // Transition to test mini-game
    this.cameras.main.fadeOut(300, 0, 0, 0)
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('TestMiniGameScene')
    })
  }

  private startChoppingMiniGame(): void {
    // Transition to chopping mini-game
    this.cameras.main.fadeOut(300, 0, 0, 0)
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ChoppingMiniGameScene')
    })
  }
}
